import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { crashLogger } from '../utils/crashLogger';

export interface User {
  id: string;
  username: string;
  role: 'admin' | 'videomaker' | 'video_editor' | 'copywriter' | 'publisher';
  // Ruolo aggiuntivo: si somma a quello principale. Chi pubblica i reel sulle
  // pagine social e per questo vede tutti i clienti con i loro dati.
  is_publisher?: boolean;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  created_at: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Cookie helper functions
const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  // Don't use Secure flag for development (HTTP)
  const isHTTPS = window.location.protocol === 'https:';
  const secureFlag = isHTTPS ? ';Secure' : '';
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/${secureFlag};SameSite=Lax`;
};

export const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

// Debug function to check cookies
const debugCookies = () => {
  console.log('🍪 All cookies:', document.cookie);
  console.log('🔍 ByMarke token in cookies:', getCookie('bymarke_token') ? 'Found' : 'Not found');
  console.log('💾 ByMarke token in localStorage:', localStorage.getItem('bymarke_token') ? 'Found' : 'Not found');
};

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  // Motivo dell'ultimo login fallito, così la schermata di accesso può
  // distinguere "credenziali errate" da "account in attesa di approvazione"
  authMessage: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  getToken: () => string | null;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  updateProfile: (profileData: ProfileUpdateData) => Promise<boolean>;
  handleApiError: (error: any) => void;
}

interface ProfileUpdateData {
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshAttempt, setLastRefreshAttempt] = useState<number>(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [errorHandlerCooldown, setErrorHandlerCooldown] = useState<number>(0);
  const [authMessage, setAuthMessage] = useState<string | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      // La pagina pubblica del cliente non tocca il login (02/11/2026): se no ricreava il cookie
      // dal localStorage e il riquadro admin del link compariva anche dopo aver cancellato i cookie.
      if (window.location.pathname.startsWith('/calendar/client/')) {
        setIsLoading(false);
        return;
      }
      debugCookies();
      
      // Check both localStorage and cookies
      let token = localStorage.getItem('bymarke_token');
      console.log('🔍 Checking localStorage token:', token ? 'Found' : 'Not found');
      
      if (!token) {
        token = getCookie('bymarke_token');
        console.log('🍪 Checking cookie token:', token ? 'Found' : 'Not found');
        if (token) {
          localStorage.setItem('bymarke_token', token);
          console.log('✅ Restored token from cookie to localStorage');
        }
      }

      if (token) {
        try {
          console.log('🔑 Attempting to validate token...');
          const response = await fetch(`${API_BASE_URL}/auth/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setUser(data.data);
              setIsAuthenticated(true);
              // Refresh token and cookie
              setCookie('bymarke_token', token, 7); // 7 days
              console.log('✅ User authenticated successfully from saved token');
            } else {
              console.log('❌ Token validation failed:', data.message);
              localStorage.removeItem('bymarke_token');
              deleteCookie('bymarke_token');
              setIsAuthenticated(false);
            }
          } else {
            console.log('❌ Auth profile request failed:', response.status);
            if (response.status !== 429) { // Don't remove token on rate limit
              localStorage.removeItem('bymarke_token');
              deleteCookie('bymarke_token');
              setIsAuthenticated(false);
            }
          }
        } catch (error) {
          console.error('❌ Auth check failed:', error);
          // Don't remove tokens on network errors, might be temporary
          if (!(error instanceof Error) || !error.message.includes('fetch')) {
            localStorage.removeItem('bymarke_token');
            deleteCookie('bymarke_token');
            setIsAuthenticated(false);
          }
        }
      } else {
        console.log('ℹ️ No authentication token found');
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setAuthMessage(null);

    try {
      console.log('API_BASE_URL:', API_BASE_URL);
      console.log('Login attempt to:', `${API_BASE_URL}/auth/login`);
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const { user, access_token, refresh_token } = data.data;
        setUser(user);
        setIsAuthenticated(true);
        localStorage.setItem('bymarke_token', access_token);
        setCookie('bymarke_token', access_token, 7); // Save cookie for 7 days
        if (refresh_token) {
          localStorage.setItem('bymarke_refresh_token', refresh_token);
        }
        console.log('✅ Login successful - Token saved to localStorage and cookie');
        console.log('🍪 Cookie set:', document.cookie.includes('bymarke_token') ? 'Success' : 'Failed');
        debugCookies();
        setIsLoading(false);
        return true;
      } else {
        console.error('Login failed:', data.message);
        setAuthMessage(data.message || null);
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthMessage('Impossibile contattare il server. Riprova.');
      setIsLoading(false);
      return false;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    const now = Date.now();
    // Evita tentativi troppo frequenti (meno di 30 secondi)
    if (isRefreshing || (now - lastRefreshAttempt < 30000)) {
      return false;
    }

    try {
      setIsRefreshing(true);
      setLastRefreshAttempt(now);

      const currentToken = localStorage.getItem('bymarke_token');
      const refreshTokenValue = localStorage.getItem('bymarke_refresh_token');

      if (!refreshTokenValue) {
        console.log('No refresh token available');
        return false;
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshTokenValue })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.access_token) {
          localStorage.setItem('bymarke_token', data.data.access_token);
          setCookie('bymarke_token', data.data.access_token, 7);
          if (data.data.refresh_token) {
            localStorage.setItem('bymarke_refresh_token', data.data.refresh_token);
          }
          console.log('✅ Token refreshed successfully');
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    } finally {
      setIsRefreshing(false);
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('bymarke_token')}`
        },
        body: JSON.stringify({ 
          current_password: currentPassword,
          new_password: newPassword 
        })
      });

      if (response.ok) {
        return true;
      } else {
        await handleApiError(response);
        return false;
      }
    } catch (error) {
      console.error('Change password error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (profileData: ProfileUpdateData): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('bymarke_token')}`
        },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser(data.data);
          return true;
        }
      } else {
        await handleApiError(response);
      }
      return false;
    } catch (error) {
      console.error('Update profile error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    if (isLoggingOut) return; // Evita chiamate multiple

    crashLogger.logEvent('LOGOUT_TRIGGERED', {
      stackTrace: new Error().stack,
      isAuthenticated,
      isRefreshing,
      timestamp: new Date().toISOString()
    });

    setIsLoggingOut(true);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('bymarke_token');
    localStorage.removeItem('bymarke_refresh_token');
    deleteCookie('bymarke_token');
    // Redirect to login page (router will handle this automatically)

    // Reset flag dopo un breve delay
    setTimeout(() => setIsLoggingOut(false), 1000);
  };

  // Check for 401/403 errors and try refresh before logout
  const handleApiError = async (response: Response) => {
    const now = Date.now();
    
    // Evita chiamate multiple ravvicinate (debouncing)
    if (now - errorHandlerCooldown < 2000) {
      crashLogger.logEvent('API_ERROR_DEBOUNCED', {
        status: response.status,
        url: response.url,
        timeSinceLast: now - errorHandlerCooldown
      });
      return;
    }
    
    setErrorHandlerCooldown(now);
    
    crashLogger.logEvent('API_ERROR_HANDLED', {
      status: response.status,
      url: response.url,
      isRefreshing,
      isLoggingOut,
      isAuthenticated,
      timeSinceLastRefresh: now - lastRefreshAttempt
    });
    
    if (response.status === 401 && !isRefreshing && !isLoggingOut && (now - lastRefreshAttempt > 10000)) {
      console.log('Token expired, trying to refresh...');
      crashLogger.logEvent('TOKEN_REFRESH_ATTEMPT', { url: response.url });
      
      const refreshed = await refreshToken();
      if (!refreshed && isAuthenticated && !isLoggingOut) {
        console.log('Refresh failed, logging out...');
        crashLogger.logEvent('REFRESH_FAILED_LOGOUT', { url: response.url });
        logout();
      }
    } else if (response.status === 403 && isAuthenticated && !isLoggingOut) {
      console.log('Access forbidden, logging out...');
      crashLogger.logEvent('FORBIDDEN_LOGOUT', { url: response.url });
      logout();
    }
  };

  // Identità stabile: mezza app la usa come dipendenza di useCallback/useEffect
  // (DataContext.loadContenuti, l'AI Assistant); rigenerarla a ogni render
  // faceva ripartire caricamenti e riaperture di chat senza motivo.
  const getToken = useCallback(() => {
    return localStorage.getItem('bymarke_token');
  }, []);

  const value = {
    isAuthenticated,
    user,
    isLoading,
    authMessage,
    login,
    logout,
    getToken,
    changePassword,
    updateProfile,
    handleApiError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.warn('useAuth called outside of AuthProvider, this might indicate a provider ordering issue');
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};